import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  SecurityContext,
  ViewChild,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AbcCore } from '@barthauer/abc/core';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import { UserFriendlyError } from '@barthauer/abc/core/error-handling';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { BehaviorSubject, merge, Observable, of, Subject } from 'rxjs';
import { catchError, mapTo, publishReplay, refCount, switchMap, takeUntil, tap } from 'rxjs/operators';
import { QrCodeScannerState, WebApiInfo } from '../models/webapi-info.model';
import { QrCodeScannerComponent } from '../qr-code-scanner/qr-code-scanner.component';
import { UserService } from '../services/user.service';
import { WebApiInfoService } from './../services/webapi-info.service';

declare var cordova: any;

@Component({
  selector: 'abc-authentication-webapi-editor',
  templateUrl: './webapi-editor.component.html',
  styleUrls: ['./webapi-editor.component.scss'],
})
export class WebApiEditorComponent implements OnInit, OnDestroy, AfterViewInit {
  public res = ResourceSet.Empty;
  @Input() errorClass: string;
  @Input() requiredSystemName: string;
  @ViewChild('abcQrCodeScanner') abcQrCodeScanner: QrCodeScannerComponent;
  private _currentBaseURL: string;
  @Input()
  get currentBaseURL(): string {
    return this._currentBaseURL;
  }
  set currentBaseURL(data) {
    this._currentBaseURL = data;
  }
  isQRCodeScanSupported: boolean = false;
  ErrorMessage: string;
  isUserFriendlyErrorMessage: boolean;
  lastWebApiValidationErrorMessage: string;
  validatedWebApiInfo$: Observable<WebApiInfo>;
  isValidatingWebApiURL$: Observable<boolean>;
  isApplyingWebApiURL: boolean;

  private readonly validateBaseURLToken$ = new BehaviorSubject<boolean>(false);
  private readonly destroy$ = new Subject<void>();
  private ApplicationTitle: string;
  private ForwardToRoutePath: string;
  private ForceReloadOnApply: boolean;
  private rootContainerDiv: HTMLElement;
  private beepSound: HTMLAudioElement;

  constructor(
    sanitizer: DomSanitizer,
    private changeDetector: ChangeDetectorRef,
    private configService: AbcConfigService,
    private webApiInfoService: WebApiInfoService,
    private router: Router,
    private route: ActivatedRoute,
    private abcCore: AbcCore,
    private snackbar: MatSnackBar,
    private resourceService: ResourceManagerService,
    private userService: UserService
  ) {
    this.route.data.pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.ApplicationTitle = data.ApplicationTitle;
        this.ForwardToRoutePath = data.ForwardTo;
        this.ForceReloadOnApply = data.ForceReloadOnApply;
      },
      error: (err) => this.abcCore.error.publish(err),
    });

    this.resetError();
    const beepSrc = sanitizer.sanitize(
      SecurityContext.RESOURCE_URL,
      sanitizer.bypassSecurityTrustResourceUrl('assets/sounds/barcodebeep.mp3')
    );
    this.beepSound = new Audio(beepSrc);
    this.beepSound.load();
  }

  async ngOnInit() {
    this.res = await this.resourceService.getResourceSet('abc-core-authentication');
    this.isQRCodeScanSupported = this.checkIfQRScanIsSupported();
    this.resetError();
    this.currentBaseURL = this.configService.configuration.WebApi.BaseURL;

    this.validatedWebApiInfo$ = this.validateBaseURLToken$.pipe(
      tap((trySaveAndQuit) => {
        this.resetError();
      }),
      switchMap((trySaveAndQuit) =>
        (this.currentBaseURL ? this.webApiInfoService.getWebApiInfo(this.currentBaseURL) : of(<WebApiInfo>null)).pipe(
          tap((info) => {
            if (info) {
              this.lastWebApiValidationErrorMessage = this.getErrorMessageIfWebApiInfoIsInvalid(info);

              if (trySaveAndQuit) {
                if (this.lastWebApiValidationErrorMessage) {
                  throw new UserFriendlyError(
                    `${this.lastWebApiValidationErrorMessage} Nur gültige URLs können übernommen werden!`
                  );
                } else {
                  this.saveBaseURLAndQuit(info.baseURL);
                }
              }
            }
          }),

          catchError((err) => {
            this.isUserFriendlyErrorMessage = err instanceof UserFriendlyError;

            if (this.isUserFriendlyErrorMessage) {
              this.ErrorMessage = this.abcCore.error.getErrorInfo(err).message;
            } else {
              this.ErrorMessage = this.abcCore.error.getFullErrorMessage(err);
            }
            return of(<WebApiInfo>null);
          })
        )
      ),
      publishReplay(1),
      refCount()
    );

    this.isValidatingWebApiURL$ = merge(
      this.validateBaseURLToken$.pipe(mapTo(true)),
      this.validatedWebApiInfo$.pipe(
        catchError(() => of(undefined)),
        mapTo(false)
      )
    ).pipe(publishReplay(1), refCount());
  }

  ngAfterViewInit(): void {
    this.rootContainerDiv = document.getElementById('rootContainer');

    this.abcQrCodeScanner.onInvisible = (state: QrCodeScannerState) => {
      if (state === QrCodeScannerState.Successfull) {
        this.beep();
        this.currentBaseURL = this.abcQrCodeScanner.result;
        this.changeDetector.detectChanges();
      }
      this.setRootVisibility(true);
    };
  }

  ngOnDestroy(): void {
    this.resetError();
    this.validateBaseURLToken$.complete();
    this.destroy$.next(); // trigger the unsubscribe
    this.destroy$.complete(); // finalize & clean up the subject stream
  }

  beep() {
    if (this.beepSound) this.beepSound.play();
  }

  private resetError(): void {
    this.ErrorMessage = null;
    this.isUserFriendlyErrorMessage = false;
  }
  private getErrorMessageIfWebApiInfoIsInvalid(webApiInfo: WebApiInfo): string {
    let errorMessage = null;

    if (webApiInfo.isBaseURLInvalid) {
      errorMessage = 'Die angegebene URL ist ungültig oder nicht erreichbar!';
    } else if (
      this.requiredSystemName &&
      webApiInfo.systemInfo?.Name.toLowerCase() !== this.requiredSystemName.toLowerCase()
    ) {
      errorMessage = `Der Datendienst ist kein ${this.requiredSystemName}-Datendienst!`;
    }

    return errorMessage;
  }

  private checkIfQRScanIsSupported(): boolean {
    return true;
  }

  scanQRCode(): void {
    this.setRootVisibility(false);
    if (navigator['app']) {
      const permissions = cordova.plugins.permissions;
      const permissionList = [permissions.CAMERA];
      permissions.checkPermission(permissions.CAMERA, (status) => {
        if (status.hasPermission) {
          this.abcQrCodeScanner.startScanning();
        } else {
          permissions.requestPermissions(
            permissionList,
            (succes) => {
              this.abcQrCodeScanner.startScanning();
            },
            (error) => {
              console.log(error);
            }
          );
        }
      });
    } else {
      this.abcQrCodeScanner.startScanning();
    }
  }

  private setRootVisibility(visible: boolean) {
    if (visible) {
      this.rootContainerDiv.className = 'root-container';
      this.rootContainerDiv.style.visibility = null;
      this.rootContainerDiv.style.display = 'flex';
    } else {
      this.rootContainerDiv.className = 'root-container-none';
    }
  }

  getWebApiQRImageURL(webApiBaseURL: string): string {
    return this.webApiInfoService.getWebApiQRImageURL(webApiBaseURL);
  }

  validateCurrentBaseURL(trySaveAndQuit: boolean): void {
    this.validateBaseURLToken$.next(trySaveAndQuit);
  }

  saveBaseURLAndQuit(webApiBaseURL: string): void {
    if (webApiBaseURL) {
      try {
        this.isApplyingWebApiURL = true;
        this.configService.setWebApiBaseURL(webApiBaseURL);
        let routePath: string;

        if (this.ForwardToRoutePath) {
          routePath = this.ForwardToRoutePath;
        } else {
          routePath = '/';
        }

        if (this.ForceReloadOnApply) {
          let path = location.href.split('?')[0].split('#')[0];
          const pos = path.lastIndexOf('/');
          path = path.substring(0, pos) + routePath;
          if (navigator['app']) {
            if (!path.endsWith('/')) {
              path += '/';
            }
            path += 'index.html';
          }
          location.assign(path);
        } else {
          this.router
            .navigate([routePath])
            .then(() => {
              if (this.ForceReloadOnApply) {
                setTimeout(() => {
                  if (navigator['app']) {
                    //navigator['app'].loadUrl('file:///android_asset/www/index.html');
                  } else {
                    location.reload();
                  }
                }, 10);
              }
            })
            .finally(() => (this.isApplyingWebApiURL = false));
        }
      } catch (error) {
        this.isApplyingWebApiURL = false;
        this.ErrorMessage = this.abcCore.error.getFullErrorMessage(error);
      }
    }
  }

  //BEC: dont use this.location.back(); for newer cordova/android versions
  cancel(): void {
    this.userService.navigateToLoginPage();
  }

  openSnackBar(text: string) {
    setTimeout(() => {
      this.snackbar.open(text, null, {
        duration: 2000,
        verticalPosition: 'top',
      });
    }, 1);
  }
}
